import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Datepicker,
} from 'components';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import moment from 'moment';
import DebouncedSelect from 'components/DebounceSelect/DebounceSelect';
import { getUsersOptions } from '../../../api/services/user';
import { getTrainingCourseOptions, getTrainingCourseDetail } from '../../../api/services/training';

const TrainingForm = ({
  state,
  dispatch,
  updateFieldCreator,
  errors,
  removeError,
  onSubmit,
  heading,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const shouldPrefillControlNumber = useRef(false);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCourse = (option) => {
    dispatch(updateFieldCreator({ name: 'course', value: option?.value }));
    if (errors.course) removeError(clearErrors(errors, 'course'));
    shouldPrefillControlNumber.current = true;
  };

  useEffect(() => {
    if (!state.course) {
      dispatch(updateFieldCreator({ name: 'competencyOptions', value: [] }));
      dispatch(updateFieldCreator({ name: 'isProgramme', value: false }));
      if (shouldPrefillControlNumber.current) {
        dispatch(updateFieldCreator({ name: 'controlNumber', value: '' }));
        shouldPrefillControlNumber.current = false;
      }
      return;
    }

    let active = true;
    (async () => {
      try {
        const res = await (await getTrainingCourseDetail(state.course)).json();
        if (!active || !res?.results?.result) return;
        const { competencies, isProgramme, controlNumber } = res.results.result;
        dispatch(
          updateFieldCreator({
            name: 'competencyOptions',
            value: (competencies || []).map((c) => ({
              value: `${c.type}:${c.id}`,
              label: c.name,
              type: c.type,
              entityId: c.id,
            })),
          }),
        );
        dispatch(updateFieldCreator({ name: 'isProgramme', value: !!isProgramme }));
        if (shouldPrefillControlNumber.current) {
          dispatch(updateFieldCreator({ name: 'controlNumber', value: controlNumber || '' }));
          shouldPrefillControlNumber.current = false;
        }
      } catch (error) {
        console.error('Error loading course detail:', error);
      }
    })();

    return () => {
      active = false;
    };
  }, [state.course]);

  useEffect(() => {
    if (!errors.controlNumberSuggestion) return;
    dispatch(updateFieldCreator({ name: 'controlNumber', value: errors.controlNumberSuggestion }));
    removeError(clearErrors(errors, 'controlNumberSuggestion'));
  }, [errors.controlNumberSuggestion]);

  const submit = () => {
    const groupedCompetencies = {
      competency: [],
      peopleCompetency: [],
      practiceCompetency: [],
      customCourseCompetency: [],
    };
    (state.competencies || []).forEach((option) => {
      if (groupedCompetencies[option.type]) {
        groupedCompetencies[option.type].push(option.entityId);
      }
    });

    const body = {
      user: state.user,
      course: state.course,
      name: state.name,
      completionDate: state.completionDate
        ? moment(state.completionDate).format('YYYY-MM-DD')
        : null,
      completionPlace: state.completionPlace,
      controlNumber: state.controlNumber,
      cpdHours: state.cpdHours === '' ? null : state.cpdHours,
      competencies: groupedCompetencies.competency,
      peopleCompetencies: groupedCompetencies.peopleCompetency,
      practiceCompetencies: groupedCompetencies.practiceCompetency,
      customCourseCompetencies: groupedCompetencies.customCourseCompetency,
      programmeSubset: state.isProgramme ? state.programmeSubset : null,
    };

    onSubmit(body, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{heading}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="user"
                  label={t('page.ma.cpdManagement.trainings.user')}
                  required
                  error={errors.user}
                >
                  <DebouncedSelect
                    id="user"
                    value={state.user}
                    intialSelectedOption={state.userOption}
                    onChange={handleChangeSelect('user', 'user')}
                    placeholder={t('page.ma.cpdManagement.trainings.userPlaceholder')}
                    fetchOptions={getUsersOptions}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="course"
                  label={t('page.ma.cpdManagement.trainings.course')}
                  required
                  error={errors.course}
                >
                  <DebouncedSelect
                    id="course"
                    value={state.course}
                    intialSelectedOption={state.courseOption}
                    onChange={handleChangeCourse}
                    placeholder={t('page.ma.cpdManagement.trainings.coursePlaceholder')}
                    fetchOptions={getTrainingCourseOptions}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="name"
                  label={t('page.ma.cpdManagement.trainings.name')}
                  required
                  error={errors.name}
                >
                  <input
                    id="name"
                    type="text"
                    placeholder={t('page.ma.cpdManagement.trainings.namePlaceholder')}
                    value={state.name}
                    onChange={handleChangeNative('name', 'name')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="completionDate"
                  label={t('page.ma.cpdManagement.trainings.completionDate')}
                  required
                  value={state.completionDate}
                  clear={() => handleChangeCustom('completionDate')(null)}
                  error={errors.completionDate}
                >
                  <Datepicker
                    id="completionDate"
                    type="text"
                    small
                    placeholderText={t('page.ma.cpdManagement.trainings.completionDate')}
                    selected={state.completionDate}
                    onChange={handleChangeCustom('completionDate', 'completionDate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="completionPlace"
                  label={t('page.ma.cpdManagement.trainings.completionPlace')}
                  error={errors.completionPlace}
                >
                  <input
                    id="completionPlace"
                    type="text"
                    placeholder={t('page.ma.cpdManagement.trainings.completionPlacePlaceholder')}
                    value={state.completionPlace}
                    onChange={handleChangeNative('completionPlace', 'completionPlace')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="controlNumber"
                  label={t('page.ma.cpdManagement.trainings.controlNumber')}
                  required
                  error={errors.controlNumber}
                  description={t('page.ma.cpdManagement.trainings.controlNumberNote')}
                >
                  <input
                    id="controlNumber"
                    type="text"
                    placeholder={
                      state.course
                        ? t('page.ma.cpdManagement.trainings.controlNumberPlaceholder')
                        : t('page.ma.cpdManagement.trainings.competenciesSelectCourseFirst')
                    }
                    value={state.controlNumber}
                    onChange={handleChangeNative('controlNumber', 'controlNumber')}
                    disabled={!state.course}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cpdHours"
                  label={t('page.ma.cpdManagement.trainings.cpdHours')}
                  error={errors.cpdHours}
                >
                  <input
                    id="cpdHours"
                    type="number"
                    step="0.5"
                    min="0"
                    placeholder={t('page.ma.cpdManagement.trainings.cpdHoursPlaceholder')}
                    value={state.cpdHours}
                    onChange={handleChangeNative('cpdHours', 'cpdHours')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="competencies"
                  label={t('page.ma.cpdManagement.trainings.competencies')}
                  error={errors.competencies}
                >
                  <Select
                    id="competencies"
                    isMulti
                    placeholder={
                      state.course
                        ? t('page.ma.cpdManagement.trainings.competenciesPlaceholder')
                        : t('page.ma.cpdManagement.trainings.competenciesSelectCourseFirst')
                    }
                    value={state.competencies}
                    options={state.competencyOptions}
                    onChange={(options) =>
                      handleChangeCustom('competencies', 'competencies')(options || [])
                    }
                    disabled={!state.course}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          {state.isProgramme && (
            <Row>
              <Col size={{ xl: 1 }}>
                <Margin bottom="20">
                  <Field
                    id="programmeSubset"
                    label={t('page.ma.cpdManagement.trainings.programmeSubset')}
                    error={errors.programmeSubset}
                  >
                    <textarea
                      id="programmeSubset"
                      rows={4}
                      placeholder={t('page.ma.cpdManagement.trainings.programmeSubsetPlaceholder')}
                      value={state.programmeSubset}
                      onChange={handleChangeNative('programmeSubset', 'programmeSubset')}
                    />
                  </Field>
                </Margin>
              </Col>
            </Row>
          )}
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

TrainingForm.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  updateFieldCreator: PropTypes.func.isRequired,
  errors: PropTypes.object,
  removeError: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  heading: PropTypes.string,
};

TrainingForm.defaultProps = {
  errors: {},
  heading: '',
};

export default TrainingForm;
